/**
 * Detects browsers running WITHOUT GPU acceleration (SwiftShader, Mesa
 * llvmpipe/softpipe, Microsoft Basic Render Driver — VMs, RDP sessions,
 * blocklisted GPUs) and tags <html> with `.mol-soft-gpu`.
 *
 * On a software renderer every animation frame is rasterized AND composited on
 * CPU inside the browser's GPU process. A decorative infinite animation (the
 * brand AnimateGradient sweep) keeps that process saturated indefinitely, which
 * starves the synchronous renderer↔GPU handshakes made during page load — in
 * the worst case the lost reply parks the renderer main thread in an untimed
 * wait and the tab hangs permanently (observed repeatedly on software-rendered
 * VMs, 2026-08-27). PAINT-HEAVY decorative animations therefore opt into
 * `animation-play-state: var(--mol-decor-anim, running)`, and `.mol-soft-gpu`
 * sets that variable to `paused` (see index.css) — the same escape hatch shape
 * as the existing `prefers-reduced-motion` blocks, with zero effect on
 * hardware-accelerated browsers. Transform/opacity-only animations (marquees,
 * floats, spinners) are cheap on a software renderer and are deliberately NOT
 * gated — they keep running (owner decision 2026-09-02).
 */
export const detectSoftwareRenderer = (): boolean => {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl')
    // No WebGL at all is itself the software-rendering signal.
    if (!gl) return true
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
    const renderer = String(
      debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    )
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return /swiftshader|llvmpipe|softpipe|software|basic render/i.test(renderer)
  } catch (_error) {
    // Probing WebGL must never break boot — assume hardware rendering on error.
    return false
  }
}

/** Tag <html> with .mol-soft-gpu when the browser is software-rendered. */
export const applySoftwareGpuMode = (): void => {
  if (detectSoftwareRenderer()) {
    document.documentElement.classList.add('mol-soft-gpu')
  }
}
